import { inject, Injectable } from "@angular/core";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { GroupSection } from "@domain/project/product-list/group-section";
import { calculateDiscountedPrice, calculateDiscountedTotal } from "@domain/project/product-list/product-list-discount.util";
import {
  additionalProductsLabel,
  attachmentInstructionsLabel,
  buildFgdsAdditionalCategoryDisplayData,
  buildFgdsPlacedGroupDisplayData,
  FgdsSectionDisplayData,
  getProductSectionHeadline,
  isAttachmentInstructionsDisplayData,
  ProductListAttachmentInstructionsDisplayData,
  ProductListGroupDisplayData,
  productsOnFloorplanLabel,
  servicesLabel,
} from "@domain/project/product-list/product-list-display-data.util";
import { AdditionalProductConfigSection, ProductListService } from "@domain/project/product-list/product-list.service";
import { ProductSection } from "@domain/project/product-list/product-section";

export interface ClipboardServiceRow {
  position: number;
  label: string;
  partNumber: string;
  notes: string;
  costs?: number;
}

interface ExcelRow {
  [ClipboardColumn.POS]: string | null;
  [ClipboardColumn.QUANTITY]: number | null;
  [ClipboardColumn.DESCRIPTION]: string;
  [ClipboardColumn.MATERIAL_NUMBER]: string | null;
  [ClipboardColumn.VISUAL_BREAK]: string;
  [ClipboardColumn.ERZ]: string;
  [ClipboardColumn.RG]: string;
  [ClipboardColumn.LIST_PRICE]: number | null;
  [ClipboardColumn.DISCOUNT]: number | null;
  [ClipboardColumn.EP]: number | null;
  [ClipboardColumn.GP]: number | null;
  [ClipboardColumn.ALT]: string;
  [ClipboardColumn.MD]: string;
}

enum ClipboardColumn {
  POS = "POS",
  QUANTITY = "QUANTITY",
  DESCRIPTION = "DESCRIPTION",
  MATERIAL_NUMBER = "MATERIAL_NUMBER",
  VISUAL_BREAK = "VISUAL_BREAK",
  ERZ = "ERZ",
  RG = "RG",
  LIST_PRICE = "LIST_PRICE",
  DISCOUNT = "DISCOUNT",
  EP = "EP",
  GP = "GP",
  ALT = "ALT",
  MD = "MD",
}

@Injectable({
  providedIn: "root",
})
export class ClipboardProductListService {
  readonly COLUMN_ORDER: ClipboardColumn[] = [
    ClipboardColumn.POS,
    ClipboardColumn.QUANTITY,
    ClipboardColumn.DESCRIPTION,
    ClipboardColumn.MATERIAL_NUMBER,
    ClipboardColumn.VISUAL_BREAK,
    ClipboardColumn.ERZ,
    ClipboardColumn.RG,
    ClipboardColumn.LIST_PRICE,
    ClipboardColumn.DISCOUNT,
    ClipboardColumn.EP,
    ClipboardColumn.GP,
    ClipboardColumn.ALT,
    ClipboardColumn.MD,
  ];

  private readonly germanPrice = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });

  private productListService = inject(ProductListService);

  build(
    groupSections: GroupSection[],
    additionalSections: AdditionalProductConfigSection[],
    rootAttachmentInstructions: ProductData[],
    serviceRows: ClipboardServiceRow[],
    showZrefPrices: boolean,
  ): string {
    const fgdsPlacedGroups = buildFgdsPlacedGroupDisplayData(
      groupSections,
      (group) => this.productListService.getAttachmentInstructionsForGroup(group),
      () => 0,
    )
      .map((group) => ({
        ...group,
        sections: group.sections.filter((section) => !isAttachmentInstructionsDisplayData(section)),
      }))
      .filter((group) => group.sections.length > 0);

    const additionalCategories = buildFgdsAdditionalCategoryDisplayData(false, additionalSections, {});

    const productSectionRows = this.addPlacedProductSections(fgdsPlacedGroups, showZrefPrices);
    const additionalSectionRows = this.addAdditionalProductSections(additionalCategories, showZrefPrices);
    const rootAttachmentInstructionRows = this.addRootAttachmentInstructions(rootAttachmentInstructions, showZrefPrices);
    const serviceRowsResult = this.addServiceRows(serviceRows);

    return this.rowsToTSV([
      ...productSectionRows,
      ...additionalSectionRows,
      ...rootAttachmentInstructionRows,
      ...serviceRowsResult,
    ]);
  }

  private addServiceRows(serviceRows: ClipboardServiceRow[]): ExcelRow[] {
    if (serviceRows.length === 0) {
      return [];
    }

    const rows: ExcelRow[] = [];
    rows.push(this.addProductHeader(servicesLabel));

    serviceRows.forEach((row) => {
      rows.push(this.addProductHeader(row.label));
      rows.push({
        POS: row.position.toString(),
        QUANTITY: 1,
        DESCRIPTION: row.notes,
        MATERIAL_NUMBER: row.partNumber,
        VISUAL_BREAK: "",
        ERZ: "",
        RG: "",
        LIST_PRICE: null,
        DISCOUNT: null,
        EP: null,
        GP: row.costs ?? null,
        ALT: "",
        MD: "",
      });
    });

    rows.push(this.createEmptyRow());
    return rows;
  }

  private addRootAttachmentInstructions(rootAttachmentInstructions: ProductData[], showZrefPrices: boolean) {
    if (rootAttachmentInstructions.length === 0) {
      return [];
    }

    const rows: ExcelRow[] = [];
    rows.push(
      this.addProductHeader(
        attachmentInstructionsLabel,
        this.calculateAttachmentInstructionsPrice(rootAttachmentInstructions, showZrefPrices),
      ),
    );
    rows.push(...this.addProductData(rootAttachmentInstructions, 1, 1, showZrefPrices));
    rows.push(this.createEmptyRow());
    return rows;
  }

  private calculateAttachmentInstructionsPrice(instructions: ProductData[], showZrefPrices: boolean): number {
    return instructions.reduce((sum, instruction) => {
      const price = (showZrefPrices ? instruction.zrefPriceData?.price : instruction.priceData?.price) ?? 0;
      return sum + calculateDiscountedTotal(price, instruction.quantity, instruction.discount ?? 0);
    }, 0);
  }

  addPlacedProductSections(
    placedGroups: ProductListGroupDisplayData<
      FgdsSectionDisplayData,
      ProductListAttachmentInstructionsDisplayData,
      GroupSection
    >[],
    showZrefPrices: boolean,
  ): ExcelRow[] {
    const rows: ExcelRow[] = [];
    const placedProductsSubtotal = showZrefPrices
      ? placedGroups.reduce((total: number, group) => total + (group.source.zrefSubTotal ?? 0), 0)
      : placedGroups.reduce((total: number, group) => total + (group.subtotal ?? 0), 0);
    rows.push(this.addProductHeader(productsOnFloorplanLabel, placedProductsSubtotal));

    placedGroups.forEach((group) => rows.push(...this.addPlacedGroup(group, showZrefPrices)));
    rows.push(this.createEmptyRow());
    return rows;
  }

  private addPlacedGroup(
    group: ProductListGroupDisplayData<FgdsSectionDisplayData, ProductListAttachmentInstructionsDisplayData, GroupSection>,
    showZrefPrices: boolean,
  ) {
    const rows: ExcelRow[] = [];
    group.sections.forEach((section) => {
      if (isAttachmentInstructionsDisplayData(section)) {
        const productSectionHeader = `${group.label} - ${section.label}`;
        rows.push(this.addProductHeader(productSectionHeader));
        rows.push(...this.addProductData(section.attachmentInstructions, 1, 1, showZrefPrices));
        return;
      }
      rows.push(...this.addProductSection(section.section, group.label, showZrefPrices));
    });
    return rows;
  }

  private addProductSection(
    productSection: ProductSection<
      | FloorplanAlarmDevice
      | FloorplanGasWarningCenter
      | FloorplanPlasticSign
      | FloorplanSignalElement
      | FloorplanTransmitter
      | FloorplanFlameDetector,
      FloorplanPlaceholder,
      | AlarmDeviceConfiguration
      | GasWarningCenterConfiguration
      | PlasticSignConfiguration
      | SignalElementConfiguration
      | TransmitterConfiguration
      | FlameDetectorConfiguration,
      ConfigProductMap
    >,
    groupName: string,
    showZrefPrices = false,
  ): ExcelRow[] {
    const placeholderProducts = this.productListService.getProductsWithIdFromPlaceholders(productSection.placeholders);
    const rows: ExcelRow[] = [];
    if (productSection.mappings.length > 0 || placeholderProducts.length > 0) {
      const productSectionHeader = `${groupName} - ${getProductSectionHeadline(productSection.productType)}`;
      rows.push(this.addProductHeader(productSectionHeader));
      productSection.mappings.forEach((mapping: ConfigProductMap) => {
        rows.push(...this.addProductData(mapping.data, mapping.placedProductsCount, undefined, showZrefPrices));

        productSection.devices.forEach((device) => {
          if (device.products) {
            device.products.forEach((product) => {
              if (product.id && this.productBelongsToConfig(device, mapping)) {
                if (!product.name) product.name = $localize`:@@customPartNumber.placeholderName:Benutzerdefinierte Sachnummer`;
                rows.push(...this.addProductData([product], 1, undefined, showZrefPrices));
              }
            });
          }
        });
      });

      if (placeholderProducts.length > 0) {
        rows.push(...this.addProductData(placeholderProducts, 1, undefined, showZrefPrices));
      }
    }
    const unspecifiedPlaceholderCount = this.productListService.countUnspecifiedPlaceholders(productSection.placeholders);
    if (unspecifiedPlaceholderCount > 0) {
      const placeholderSectionHeader = `${groupName} - ${getProductSectionHeadline(productSection.productType, true)}`;
      rows.push(this.addProductHeader(placeholderSectionHeader));
      const unspecifiedPlaceholder = new PlaceholderProduct(
        $localize`:@@productList.placeholder.description:Platzhalter (noch zu konfigurieren)`,
        "",
        productSection.placeholderPositionPrefix,
      );
      rows.push(...this.addProductData([unspecifiedPlaceholder], unspecifiedPlaceholderCount));
    }
    return rows;
  }

  private addProductData(
    productData: ProductData[] | PlaceholderProduct[],
    placedProductCount: number,
    maxQuantity?: number,
    showZrefPrices = false,
  ): ExcelRow[] {
    const rows: ExcelRow[] = [];
    productData.forEach((data: ProductData | PlaceholderProduct) => {
      const quantity = maxQuantity ?? (data instanceof ProductData ? data.quantity * placedProductCount : placedProductCount);
      const price =
        data instanceof ProductData
          ? showZrefPrices
            ? data.zrefPriceData?.price
            : data.priceData?.price
          : showZrefPrices
            ? undefined
            : data.price?.price;
      const discount = data.discount ?? 0;
      const discountedPrice = price == null ? undefined : calculateDiscountedPrice(price, discount);
      rows.push({
        POS: data.positionId ?? null,
        QUANTITY: quantity,
        DESCRIPTION: data.name,
        MATERIAL_NUMBER: data.id ?? null,
        VISUAL_BREAK: "",
        ERZ: "",
        RG: "",
        LIST_PRICE: price ?? null,
        DISCOUNT: discount,
        EP: discountedPrice ?? null,
        GP: discountedPrice != null ? discountedPrice * quantity : null,
        ALT: "",
        MD: "",
      });
    });
    return rows;
  }

  private addProductHeader(headline: string, subtotal?: number): ExcelRow {
    return {
      POS: null,
      QUANTITY: null,
      DESCRIPTION: headline,
      MATERIAL_NUMBER: "",
      VISUAL_BREAK: "",
      ERZ: "",
      RG: "",
      LIST_PRICE: null,
      DISCOUNT: null,
      EP: null,
      GP: subtotal ?? null,
      ALT: "",
      MD: "",
    };
  }

  private productBelongsToConfig(
    product:
      | FloorplanGasWarningCenter
      | FloorplanAlarmDevice
      | FloorplanSignalElement
      | FloorplanPlasticSign
      | FloorplanTransmitter
      | FloorplanFlameDetector,
    mapping: ConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }

  private addAdditionalProductSections(
    additionalCategories: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>,
    showZrefPrices: boolean,
  ): ExcelRow[] {
    if (additionalCategories.length === 0) {
      return [];
    }

    const rows: ExcelRow[] = [];
    const additionalProductsSubtotal = showZrefPrices
      ? additionalCategories.reduce((total, category) => total + (category.zrefSubTotal ?? 0), 0)
      : additionalCategories.reduce((total, category) => total + (category.subtotal ?? 0), 0);
    rows.push(this.addProductHeader(additionalProductsLabel, additionalProductsSubtotal));

    additionalCategories.forEach((category) => {
      rows.push(...this.addAdditionalCategory(category, showZrefPrices));
    });
    rows.push(this.createEmptyRow());
    return rows;
  }

  private addAdditionalCategory(
    category: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>[number],
    showZrefPrices: boolean,
  ): ExcelRow[] {
    const rows: ExcelRow[] = [];
    rows.push(this.addProductHeader(category.label));
    category.configs.forEach((config) => {
      rows.push(this.addAdditionalConfig(config, showZrefPrices));
      rows.push(...this.addProductData(config.subproducts, config.savedQuantity, undefined, showZrefPrices));
    });
    return rows;
  }

  private addAdditionalConfig(
    config: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>[number]["configs"][number],
    showZrefPrices = false,
  ) {
    const mainProduct = config.mainProduct;
    const unitPrice = mainProduct
      ? showZrefPrices
        ? mainProduct.zrefPriceData?.price
        : mainProduct.priceData?.price
      : showZrefPrices
        ? (config.zrefUnitPrice ?? undefined)
        : (config.unitPrice ?? 0);
    const discount = mainProduct?.discount ?? 0;
    const discountedUnitPrice = unitPrice == null ? undefined : calculateDiscountedPrice(unitPrice, discount);
    const row: ExcelRow = {
      POS: config.positionNumber ?? null,
      QUANTITY: config.savedQuantity,
      DESCRIPTION: config.name,
      MATERIAL_NUMBER: config.mainProductId ?? "--",
      VISUAL_BREAK: "",
      ERZ: "",
      RG: "",
      LIST_PRICE: unitPrice ?? null,
      DISCOUNT: discount,
      EP: discountedUnitPrice ?? null,
      GP: discountedUnitPrice != null ? discountedUnitPrice * config.savedQuantity : null,
      ALT: "",
      MD: "",
    };
    return row;
  }

  private createEmptyRow(): ExcelRow {
    return {
      POS: null,
      QUANTITY: null,
      DESCRIPTION: "",
      MATERIAL_NUMBER: null,
      VISUAL_BREAK: "",
      ERZ: "",
      RG: "",
      LIST_PRICE: null,
      DISCOUNT: null,
      EP: null,
      GP: null,
      ALT: "",
      MD: "",
    };
  }

  private rowsToTSV(rows: ExcelRow[]): string {
    return rows.map((row) => this.COLUMN_ORDER.map((column) => this.toTsvValue(row[column], column)).join("\t")).join("\n");
  }

  private toTsvValue(value: string | number | null, column: ClipboardColumn): string {
    if (value == null) return "";

    if (typeof value === "number") {
      // Anzahl is always a whole unit count — it must not be locale-formatted like a price (e.g. "2", not "2,00").
      if (column === ClipboardColumn.QUANTITY) return value.toString();
      if (column === ClipboardColumn.DISCOUNT) return this.germanPrice.format(value / 100);
      return this.germanPrice.format(value);
    }

    return value;
  }
}
